import { useEffect, useState } from "react";
import Breadcrumbs from "./Breadcrumbs";
import Seo from "./Seo";
import Callout from "./Callout";
import Reveal from "./Reveal";
import OddsCalculator from "./tools/OddsCalculator";
import CombiSimulator from "./tools/CombiSimulator";
import BonusSimulator from "./tools/BonusSimulator";
import ResponsibleQuiz from "./tools/ResponsibleQuiz";
import { MarginDiagram, OddsLadderDiagram, CombiDiagram } from "./Diagrams";
import { BASE_URL, SITE_NAME } from "../config/site";
import { CheckCircle2, ListTree } from "lucide-react";

const TOOLS = {
  odds: <OddsCalculator />,
  combi: <CombiSimulator />,
  bonus: <BonusSimulator />,
  quiz: <ResponsibleQuiz />,
};

const DIAGRAMS = {
  margin: <MarginDiagram />,
  ladder: <OddsLadderDiagram />,
  combi: <CombiDiagram />,
};

function Block({ block }) {
  switch (block.t) {
    case "p":
      return <p dangerouslySetInnerHTML={{ __html: block.html }} />;
    case "h3":
      return <h3 className="mt-8 font-display text-xl font-semibold text-[#0B1F3A]">{block.text}</h3>;
    case "list":
      return (
        <ul className="my-4 list-disc space-y-2 pl-6 marker:text-[#2BB08A]">
          {block.items.map((it, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: it }} />
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="my-4 list-decimal space-y-2 pl-6 marker:font-display marker:font-semibold marker:text-[#2BB08A]">
          {block.items.map((it, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: it }} />
          ))}
        </ol>
      );
    case "checklist":
      return (
        <ul className="my-4 space-y-2.5">
          {block.items.map((it, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <CheckCircle2 className="mt-1 h-4.5 w-4.5 h-5 w-5 shrink-0 text-[#2BB08A]" aria-hidden="true" />
              <span dangerouslySetInnerHTML={{ __html: it }} />
            </li>
          ))}
        </ul>
      );
    case "table":
      return (
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                {block.head.map((h, i) => (
                  <th key={i} scope="col">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j} dangerouslySetInnerHTML={{ __html: cell }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "callout":
      return (
        <Callout kind={block.kind} title={block.title}>
          <span dangerouslySetInnerHTML={{ __html: block.html }} />
        </Callout>
      );
    case "tool":
      return TOOLS[block.name] || null;
    case "diagram":
      return DIAGRAMS[block.name] || null;
    default:
      return null;
  }
}

export default function ArticleLayout({ article }) {
  const [active, setActive] = useState(null);
  const sections = article.sections || [];

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setActive(e.target.id));
      },
      { rootMargin: "-20% 0px -70% 0px" },
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [sections]);

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: `${BASE_URL}/` },
      ...(article.category
        ? [{ "@type": "ListItem", position: 2, name: article.category }]
        : []),
      {
        "@type": "ListItem",
        position: article.category ? 3 : 2,
        name: article.h1,
        item: `${BASE_URL}${article.path}`,
      },
    ],
  };

  const faqLd =
    article.faq && article.faq.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: article.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;

  return (
    <>
      <Seo
        title={article.title}
        description={article.description}
        path={article.path}
        jsonLd={faqLd ? [breadcrumbLd, faqLd] : [breadcrumbLd]}
      />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="lg:flex lg:gap-12">
          <article className="min-w-0 max-w-[760px] flex-1" data-testid="article-content">
            <Breadcrumbs
              items={[
                ...(article.category ? [{ label: article.category }] : []),
                { label: article.h1 },
              ]}
            />
            <Reveal>
              <h1 className="font-display text-3xl font-bold tracking-tight text-[#0B1F3A] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.15]">
                {article.h1}
              </h1>
            </Reveal>
            {article.intro && (
              <p className="mt-5 text-lg leading-relaxed text-slate-600">{article.intro}</p>
            )}

            <div className="article-body mt-8">
              {sections.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-24">
                  <Reveal>
                    <h2 className="mb-4 mt-12 font-display text-2xl font-semibold tracking-tight text-[#0B1F3A] sm:text-[1.7rem]">
                      {s.h2}
                    </h2>
                  </Reveal>
                  {s.blocks.map((b, i) => (
                    <Block key={i} block={b} />
                  ))}
                </section>
              ))}

              {article.faq && article.faq.length > 0 && (
                <section id="faq" className="scroll-mt-24">
                  <Reveal>
                    <h2 className="mb-4 mt-12 font-display text-2xl font-semibold tracking-tight text-[#0B1F3A] sm:text-[1.7rem]">
                      Questions fréquentes
                    </h2>
                  </Reveal>
                  <div className="space-y-3">
                    {article.faq.map((f, i) => (
                      <details
                        key={i}
                        data-testid={`faq-item-${i}`}
                        className="group rounded-lg border border-slate-200 bg-white open:border-[#2BB08A]"
                      >
                        <summary className="cursor-pointer list-none px-5 py-4 font-display text-[15px] font-semibold text-[#0B1F3A] transition-colors hover:text-[#2BB08A]">
                          {f.q}
                        </summary>
                        <p className="px-5 pb-4 text-[15px] leading-relaxed text-slate-600">
                          {f.a}
                        </p>
                      </details>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>

          {sections.length > 2 && (
            <aside className="hidden w-64 shrink-0 lg:block" aria-label="Sommaire">
              <div className="sticky top-24 rounded-xl border border-slate-200 bg-white p-5">
                <p className="mb-3 flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <ListTree className="h-4 w-4 text-[#2BB08A]" aria-hidden="true" />
                  Sommaire
                </p>
                <ul className="space-y-1.5" data-testid="article-toc">
                  {sections.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        data-testid={`toc-${s.id}`}
                        className={`block rounded-md px-2.5 py-1.5 text-[13px] leading-snug transition-colors ${
                          active === s.id
                            ? "bg-[#E8F8F3] font-semibold text-[#064E3B]"
                            : "text-slate-600 hover:bg-[#F3F5F8] hover:text-[#0B1F3A]"
                        }`}
                      >
                        {s.h2}
                      </a>
                    </li>
                  ))}
                  {article.faq && article.faq.length > 0 && (
                    <li>
                      <a
                        href="#faq"
                        data-testid="toc-faq"
                        className="block rounded-md px-2.5 py-1.5 text-[13px] text-slate-600 transition-colors hover:bg-[#F3F5F8]"
                      >
                        Questions fréquentes
                      </a>
                    </li>
                  )}
                </ul>
                <p className="mt-4 border-t border-slate-100 pt-3 text-[11px] leading-relaxed text-slate-400">
                  {SITE_NAME} — guide indépendant, sans affiliation.
                </p>
              </div>
            </aside>
          )}
        </div>
      </div>
    </>
  );
}
